import { For, Index, Show, createMemo, createSignal, type JSX } from "solid-js";
import {
  atRatio,
  atValue,
  isClosed,
  labelAnchor,
  pointsAttribute,
  ring,
  runsOf,
  type RadarGeometry,
} from "../lib/radar.ts";
import styles from "./RadarChart.module.scss";

export type RadarAxis = {
  id: string;
  label: string;
  /**
   * How much this axis counts to whoever the wheel is read for. Drawn as a dot
   * beside the label in the tone's colour; `muted` fades the axis, for one that
   * has been rated away entirely.
   */
  mark?: RadarMark;
};

export type RadarMark = {
  /** A class name on the chart's own stylesheet that sets `--mark`; see `markTones`. */
  tone: string;
  /** Spoken and shown in the tooltip: what the mark stands for. */
  label: string;
  muted?: boolean;
};

/** The tones a mark may carry, the same five as the relevancy scale. */
export const markTones = ["muchLess", "less", "equal", "more", "muchMore"] as const;

export type RadarSeries = {
  id: string;
  label: string;
  /** Any CSS colour, including a custom property, for the outline and markers. */
  color: string;
  /** The area wash under the outline; omitted leaves the shape unfilled. */
  fill?: string;
  /**
   * The dot shape on every value. A second cue beside the colour, so the two
   * shapes stay apart for a reader who cannot tell the hues apart.
   */
  marker?: "circle" | "square";
  /** One entry per axis, in axis order. `null` is a gap, never a zero. */
  values: readonly (number | null)[];
  /** Anything the legend should say after the label; the tooltip keeps the label alone. */
  detail?: JSX.Element;
};

export type RadarChartProps = {
  axes: readonly RadarAxis[];
  series: readonly RadarSeries[];
  /** The value the outermost ring stands for. */
  max: number;
  /** How many rings to draw between the centre and the edge. */
  rings?: number;
  /** Writes a value the way the rest of the screen writes it. */
  format: (value: number) => string;
  /** What a gap reads as, in the tooltip and in the table. */
  missingLabel: string;
  /** Names the axis column of the table behind the chart. */
  axisHeader: string;
  /** Names the marks' column of that table; needed once any axis carries a mark. */
  markHeader?: string;
  /** Describes the whole chart for a reader who cannot see it. */
  caption: string;
  /** Shown instead of the wheel when there is no axis to draw. */
  emptyLabel: string;
};

/** Beyond this many axes the labels collide, so the tooltip carries them alone. */
const labelBudget = 16;

/*
 * A landscape box, because the labels are not spread evenly around the wheel:
 * the ones to the left and right run outwards for their full length, while the
 * ones at the top and bottom are centred and need almost no room. Widening
 * only the axis that is short of space keeps the wheel itself large.
 */
const width = 480;
const height = 360;
const radius = 128;
const labelGap = 10;
const labelBudgetChars = 20;

/**
 * A radar chart: one wheel, any number of series, one value per axis.
 *
 * It knows nothing about what it plots. Callers hand it axes, series and a
 * scale, which is what makes it worth having as its own component: comparing
 * two products is one use, and personas or a product against its earlier self
 * are the same picture with different labels.
 *
 * The chart itself is decoration for assistive technology; the table underneath
 * carries the same numbers and is what a screen reader announces.
 */
export const RadarChart = (props: RadarChartProps) => {
  const [hovered, setHovered] = createSignal<number | null>(null);

  const geometry = createMemo<RadarGeometry>(() => ({
    centre: { x: width / 2, y: height / 2 },
    radius,
    axes: props.axes.length,
    max: props.max,
  }));

  const ringRatios = createMemo(() => {
    const count = props.rings ?? 4;
    return Array.from({ length: count }, (_, index) => (index + 1) / count);
  });

  const showLabels = () => props.axes.length <= labelBudget;
  const marked = () => props.axes.some((axis) => axis.mark !== undefined);
  const muted = (index: number) => props.axes[index]?.mark?.muted === true;

  const valueAt = (series: RadarSeries, index: number): number | null => series.values[index] ?? null;

  /** Everything the tooltip needs about the axis under the pointer, or nothing. */
  const reading = createMemo(() => {
    const index = hovered();
    const axis = index === null ? undefined : props.axes[index];
    if (index === null || axis === undefined) return undefined;

    return {
      axis,
      anchor: labelAnchor(index, props.axes.length),
      at: atRatio(geometry(), index, 1),
      values: props.series.map((series) => ({ series, value: valueAt(series, index) })),
    };
  });

  return (
    <div class={styles.chart}>
      <Show when={props.axes.length > 0} fallback={<p class={styles.empty}>{props.emptyLabel}</p>}>
        <div class={styles.stage}>
          <svg class={styles.wheel} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
            <For each={ringRatios()}>
              {(ratio) => (
                <polygon
                  class={styles.ring}
                  points={pointsAttribute(ring(geometry(), ratio))}
                />
              )}
            </For>

            <Index each={props.axes}>
              {(_, index) => (
                <line
                  class={`${styles.spoke} ${hovered() === index ? styles.spokeLit : ""} ${
                    muted(index) ? styles.spokeMuted : ""
                  }`}
                  x1={geometry().centre.x}
                  y1={geometry().centre.y}
                  x2={atRatio(geometry(), index, 1).x}
                  y2={atRatio(geometry(), index, 1).y}
                />
              )}
            </Index>

            <For each={props.series}>{(series) => <SeriesShape geometry={geometry()} series={series} />}</For>

            <Show when={showLabels()}>
              <Index each={props.axes}>
                {(axis, index) => (
                  <>
                    <text
                      class={`${styles.axisLabel} ${hovered() === index ? styles.axisLabelLit : ""} ${
                        muted(index) ? styles.axisLabelMuted : ""
                      }`}
                      x={atRatio(geometry(), index, 1 + labelGap / radius).x}
                      y={atRatio(geometry(), index, 1 + labelGap / radius).y}
                      text-anchor={labelAnchor(index, props.axes.length)}
                      dominant-baseline="middle"
                    >
                      {shorten(axis().label)}
                    </text>
                    {/*
                      The mark sits on the spoke's end, just inside the label, so
                      the eye takes the weight in on the way from the wheel to
                      the name.
                    */}
                    <Show when={axis().mark}>
                      {(mark) => (
                        <circle
                          class={`${styles.mark} ${styles[mark().tone] ?? ""}`}
                          cx={atRatio(geometry(), index, 1 + 3 / radius).x}
                          cy={atRatio(geometry(), index, 1 + 3 / radius).y}
                          r={3}
                        />
                      )}
                    </Show>
                  </>
                )}
              </Index>
            </Show>

            <Index each={props.axes}>
              {(_, index) => (
                <polygon
                  class={styles.wedge}
                  points={pointsAttribute(wedge(geometry(), index))}
                  onMouseEnter={() => setHovered(index)}
                  onMouseLeave={() => setHovered(null)}
                />
              )}
            </Index>
          </svg>

          <Show when={reading()}>
            {(current) => (
              <div
                class={`${styles.tooltip} ${styles[`tip${current().anchor}`]}`}
                style={{
                  left: `${(current().at.x / width) * 100}%`,
                  top: `${(current().at.y / height) * 100}%`,
                }}
              >
                <p class={styles.tipTitle}>{current().axis.label}</p>
                <Show when={current().axis.mark}>
                  {(mark) => (
                    <p class={`${styles.tipMark} ${styles[mark().tone] ?? ""}`}>{mark().label}</p>
                  )}
                </Show>
                <For each={current().values}>
                  {(entry) => (
                    <p class={styles.tipRow}>
                      <span
                        class={`${styles.swatch} ${entry.series.marker === "square" ? styles.square : ""}`}
                        style={{ "background-color": entry.series.color }}
                      />
                      <span class={styles.tipName}>{entry.series.label}</span>
                      <span class={styles.tipValue}>
                        {entry.value === null ? props.missingLabel : props.format(entry.value)}
                      </span>
                    </p>
                  )}
                </For>
              </div>
            )}
          </Show>
        </div>

        <ul class={styles.legend}>
          <For each={props.series}>
            {(series) => (
              <li class={styles.legendItem}>
                <span
                  class={`${styles.swatch} ${series.marker === "square" ? styles.square : ""}`}
                  style={{ "background-color": series.color }}
                />
                <span>{series.label}</span>
                <Show when={series.detail !== undefined}>{series.detail}</Show>
              </li>
            )}
          </For>
        </ul>
      </Show>

      <table class={styles.readout}>
        <caption>{props.caption}</caption>
        <thead>
          <tr>
            <th scope="col">{props.axisHeader}</th>
            <Show when={marked()}>
              <th scope="col">{props.markHeader}</th>
            </Show>
            <For each={props.series}>{(series) => <th scope="col">{series.label}</th>}</For>
          </tr>
        </thead>
        <tbody>
          <Index each={props.axes}>
            {(axis, index) => (
              <tr>
                <th scope="row">{axis().label}</th>
                <Show when={marked()}>
                  <td>{axis().mark?.label ?? ""}</td>
                </Show>
                <For each={props.series}>
                  {(series) => (
                    <td>
                      {valueAt(series, index) === null
                        ? props.missingLabel
                        : props.format(valueAt(series, index) ?? 0)}
                    </td>
                  )}
                </For>
              </tr>
            )}
          </Index>
        </tbody>
      </table>
    </div>
  );
};

type SeriesShapeProps = {
  geometry: RadarGeometry;
  series: RadarSeries;
};

/**
 * One series as outline and markers. A series that reaches every axis closes
 * into a filled polygon; one with a gap stays an open line, because a fill
 * would draw an area across data that is not there.
 */
const SeriesShape = (props: SeriesShapeProps): JSX.Element => {
  const runs = createMemo(() => runsOf(props.geometry, props.series.values));
  const closed = createMemo(() => isClosed(props.series.values));

  return (
    <g>
      <Show
        when={closed()}
        fallback={
          <For each={runs()}>
            {(run) => (
              <Show when={run.points.length > 1}>
                <polyline
                  class={styles.outline}
                  points={pointsAttribute(run.points)}
                  fill="none"
                  stroke={props.series.color}
                />
              </Show>
            )}
          </For>
        }
      >
        <polygon
          class={styles.outline}
          points={pointsAttribute(runs()[0]?.points ?? [])}
          fill={props.series.fill ?? "none"}
          stroke={props.series.color}
        />
      </Show>
      <Index each={props.series.values}>
        {(value, index) => (
          <Show when={value() !== null}>
            <Marker
              at={atValue(props.geometry, index, value() ?? 0)}
              color={props.series.color}
              marker={props.series.marker ?? "circle"}
            />
          </Show>
        )}
      </Index>
    </g>
  );
};

type MarkerProps = {
  at: { x: number; y: number };
  color: string;
  marker: "circle" | "square";
};

const Marker = (props: MarkerProps): JSX.Element => (
  <Show
    when={props.marker === "square"}
    fallback={<circle class={styles.marker} cx={props.at.x} cy={props.at.y} r={3.4} fill={props.color} />}
  >
    <rect
      class={styles.marker}
      x={props.at.x - 3}
      y={props.at.y - 3}
      width={6}
      height={6}
      fill={props.color}
    />
  </Show>
);

/** The hit area of one axis: the slice of the wheel that belongs to it. */
const wedge = (geometry: RadarGeometry, index: number) => {
  const half = 0.5;
  const edge = (offset: number) => {
    const angle = ((index + offset) / geometry.axes) * 2 * Math.PI - Math.PI / 2;
    const reach = geometry.radius * 1.22;
    return {
      x: geometry.centre.x + Math.cos(angle) * reach,
      y: geometry.centre.y + Math.sin(angle) * reach,
    };
  };

  return [geometry.centre, edge(-half), edge(0), edge(half)];
};

/** Long labels would run off the wheel; the tooltip and the table keep them whole. */
const shorten = (label: string): string =>
  label.length <= labelBudgetChars ? label : `${label.slice(0, labelBudgetChars - 1).trimEnd()}…`;
